import React, { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Plus, RefreshCw, Pencil, Trash2, X, Search, Calendar,
  Clock, FileText, Loader2, AlertCircle, CheckCircle2,
  ToggleLeft, ToggleRight, Eye, ChevronLeft, ChevronRight,
} from "lucide-react";

import {
  useAdminGetExamCallsQuery,
  useAdminCreateExamCallMutation,
  useAdminUpdateExamCallMutation,
  useAdminDeleteExamCallMutation,
} from "../../api/examCallsApi";
import { useAdminGetExamsQuery, useAdminGetDietsQuery } from "../../api/examsApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

/** Calls per page in the admin table. */
const PER_PAGE = 10;

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function safeMsg(err: any) {
  return err?.data?.message || err?.data?.error || err?.message || "Something went wrong";
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// The backend stores/returns opens_at & closes_at in UTC. A <input type="datetime-local">
// works in the browser's LOCAL zone, so we must convert both ways or every time
// drifts by the local UTC offset (e.g. +1h in WAT).

// UTC ISO from the server -> value for a datetime-local input (local wall-clock).
function toLocalInput(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

// datetime-local value (local wall-clock) -> absolute UTC ISO for the server.
function toUtcIso(local?: string): string | null {
  if (!local) return null;
  const d = new Date(local); // a datetime-local string parses as LOCAL time
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function callStatus(call: any): { label: string; cls: string } {
  const now = new Date();
  if (!call.is_active) return { label: "Inactive", cls: "bg-gray-100 text-gray-600" };
  const opens = call.opens_at ? new Date(call.opens_at) : null;
  const closes = call.closes_at ? new Date(call.closes_at) : null;
  if (opens && now < opens) return { label: "Upcoming", cls: "bg-amber-100 text-amber-700" };
  if (closes && now > closes) return { label: "Closed", cls: "bg-red-100 text-red-700" };
  return { label: "Open", cls: "bg-green-100 text-green-700" };
}

/* ------------------------------------------------------------------ */
/* Modal                                                                */
/* ------------------------------------------------------------------ */
type ModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
};
function Modal({ open, title, onClose, children }: ModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Call Form                                                            */
/* ------------------------------------------------------------------ */
type CallFormProps = {
  initial?: any;
  exams: any[];
  diets: any[];
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
  error?: string | null;
};

function CallForm({ initial, exams, diets, onSubmit, onCancel, submitting, error }: CallFormProps) {
  const [form, setForm] = useState({
    exam_id: initial?.exam_id ?? "",
    exam_diet_id: initial?.exam_diet_id ?? "",
    title: initial?.title ?? "",
    opens_at: toLocalInput(initial?.opens_at),
    closes_at: toLocalInput(initial?.closes_at),
    application_fee: initial?.application_fee ?? "",
    instructions: initial?.instructions ?? "",
    requirements: Array.isArray(initial?.requirements)
      ? initial.requirements.join("\n")
      : (initial?.requirements ?? ""),
    is_active: initial?.is_active ?? true,
  });

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  // A diet belongs to exactly one exam, so only that exam's diets are valid here.
  const dietsForExam = useMemo(
    () => (form.exam_id ? diets.filter((d: any) => String(d.exam_id) === String(form.exam_id)) : []),
    [diets, form.exam_id]
  );

  // Changing the exam invalidates a diet chosen under the previous one.
  const selectExam = (examId: string) =>
    setForm(f => ({
      ...f,
      exam_id: examId,
      exam_diet_id: diets.some(
        (d: any) => String(d.id) === String(f.exam_diet_id) && String(d.exam_id) === String(examId)
      )
        ? f.exam_diet_id
        : "",
    }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: any = {
      exam_id: form.exam_id,
      exam_diet_id: form.exam_diet_id,
      opens_at: toUtcIso(form.opens_at),
      closes_at: toUtcIso(form.closes_at),
      is_active: form.is_active,
    };
    if (form.title.trim()) payload.title = form.title.trim();
    if (form.application_fee !== "") payload.application_fee = Number(form.application_fee);
    if (form.instructions.trim()) payload.instructions = form.instructions.trim();
    if (form.requirements.trim()) {
      payload.requirements = form.requirements.split("\n").map((s: string) => s.trim()).filter(Boolean);
    }
    await onSubmit(payload);
  };

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
  const labelCls = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Exam + Diet */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Exam *</label>
          <select
            required
            value={form.exam_id}
            onChange={e => selectExam(e.target.value)}
            className={inputCls}
          >
            <option value="">Select exam…</option>
            {exams.map((ex: any) => (
              <option key={ex.id} value={ex.id}>{ex.code} — {ex.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Diet *</label>
          <select
            required
            disabled={!form.exam_id}
            value={form.exam_diet_id}
            onChange={e => set("exam_diet_id", e.target.value)}
            className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed`}
          >
            <option value="">
              {!form.exam_id
                ? "Select an exam first…"
                : dietsForExam.length === 0
                  ? "No diets for this exam"
                  : "Select diet…"}
            </option>
            {dietsForExam.map((d: any) => (
              <option key={d.id} value={d.id}>{d.title}</option>
            ))}
          </select>
          {form.exam_id && dietsForExam.length === 0 && (
            <p className="mt-1 text-xs text-amber-600">
              This exam has no diets yet — create one before calling it.
            </p>
          )}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className={labelCls}>Call Title (optional)</label>
        <input
          type="text"
          value={form.title}
          onChange={e => set("title", e.target.value)}
          placeholder="e.g. Part 1 Exam — April 2025"
          className={inputCls}
        />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Opens At *</label>
          <input
            type="datetime-local"
            required
            value={form.opens_at}
            onChange={e => set("opens_at", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Closes At *</label>
          <input
            type="datetime-local"
            required
            value={form.closes_at}
            onChange={e => set("closes_at", e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* Fee */}
      <div>
        <label className={labelCls}>Application Fee (₦)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={form.application_fee}
          onChange={e => set("application_fee", e.target.value)}
          placeholder="0.00"
          className={inputCls}
        />
      </div>

      {/* Instructions */}
      <div>
        <label className={labelCls}>Instructions</label>
        <textarea
          rows={3}
          value={form.instructions}
          onChange={e => set("instructions", e.target.value)}
          placeholder="General instructions for applicants…"
          className={inputCls}
        />
      </div>

      {/* Requirements */}
      <div>
        <label className={labelCls}>Requirements (one per line)</label>
        <textarea
          rows={3}
          value={form.requirements}
          onChange={e => set("requirements", e.target.value)}
          placeholder={"Valid NIQS membership\nRecent passport photograph\nBank teller / proof of payment"}
          className={inputCls}
        />
      </div>

      {/* Active toggle */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => set("is_active", !form.is_active)}
          className="flex items-center gap-2 text-sm text-gray-700"
        >
          {form.is_active
            ? <ToggleRight className="w-6 h-6 text-green-600" />
            : <ToggleLeft className="w-6 h-6 text-gray-400" />}
          {form.is_active ? "Active" : "Inactive"}
        </button>
        <span className="text-xs text-gray-400">(toggle to enable/disable this call)</span>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {initial ? "Update Call" : "Create Call"}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Delete confirm                                                        */
/* ------------------------------------------------------------------ */
function DeleteConfirm({
  call, onConfirm, onCancel, loading, error,
}: {
  call: any; onConfirm: () => void; onCancel: () => void; loading: boolean; error?: string | null;
}) {
  const label = call.title || call.exam?.title || `Call #${call.id}`;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700">
        Are you sure you want to delete <strong>{label}</strong>? This action cannot be undone and will also remove any linked data.
      </p>
      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">{error}</div>
      )}
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Delete
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Page                                                            */
/* ------------------------------------------------------------------ */
type ModalMode = "create" | "edit" | "delete" | "view" | null;

export default function AdminExamCallsPage() {
  const [q, setQ] = useState("");
  const [examFilter, setExamFilter] = useState("");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<{ mode: ModalMode; call?: any }>({ mode: null });
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: calls = [], isLoading, isFetching, error: loadError, refetch } = useAdminGetExamCallsQuery();
  const { data: exams = [] } = useAdminGetExamsQuery();
  const { data: diets = [] } = useAdminGetDietsQuery();

  const [createCall, { isLoading: creating }] = useAdminCreateExamCallMutation();
  const [updateCall, { isLoading: updating }] = useAdminUpdateExamCallMutation();
  const [deleteCall, { isLoading: deleting }] = useAdminDeleteExamCallMutation();

  // The route grants exam.diet.access, which is enough to READ the calls. Every
  // button below writes, and the API holds those behind exam.diet.manage.
  const { can } = useAccess();
  const canManage = can.perm(PERMS.exam.dietManage);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return calls.filter((c: any) => {
      if (examFilter && String(c.exam_id ?? c.exam?.id) !== String(examFilter)) return false;
      if (!s) return true;
      const blob = `${c.title ?? ""} ${c.exam?.title ?? ""} ${c.exam?.code ?? ""} ${c.diet?.title ?? ""}`.toLowerCase();
      return blob.includes(s);
    });
  }, [calls, q, examFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  // Narrowing the list (or deleting the last row on a page) can leave `page`
  // past the end — clamp rather than render an empty table.
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
    [filtered, safePage]
  );

  // Any change to the query or the filter starts again from the first page.
  useEffect(() => { setPage(1); }, [q, examFilter]);

  const closeModal = () => { setModal({ mode: null }); setFormError(null); setDeleteError(null); };

  const handleCreate = async (data: any) => {
    setFormError(null);
    try {
      await createCall(data).unwrap();
      closeModal();
    } catch (err) {
      setFormError(safeMsg(err));
    }
  };

  const handleEdit = async (data: any) => {
    if (!modal.call) return;
    setFormError(null);
    try {
      await updateCall({ id: modal.call.id, body: data }).unwrap();
      closeModal();
    } catch (err) {
      setFormError(safeMsg(err));
    }
  };

  const handleDelete = async () => {
    if (!modal.call) return;
    setDeleteError(null);
    try {
      await deleteCall(modal.call.id).unwrap();
      closeModal();
    } catch (err) {
      setDeleteError(safeMsg(err));
    }
  };

  // Follows the filters, so the tiles always describe the list beneath them.
  const stats = useMemo(() => ({
    total: filtered.length,
    open: filtered.filter((c: any) => callStatus(c).label === "Open").length,
    upcoming: filtered.filter((c: any) => callStatus(c).label === "Upcoming").length,
    closed: filtered.filter((c: any) => ["Closed", "Inactive"].includes(callStatus(c).label)).length,
  }), [filtered]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">

      {/* Header */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">Exam Calls</h1>
              <p className="text-sm text-gray-500">Manage exam application windows and requirements</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
            {canManage && (
              <button
                onClick={() => { setModal({ mode: "create" }); setFormError(null); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" /> New Call
              </button>
            )}
          </div>
        </div>

        {/* Search + exam filter */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by title, exam, diet…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
          <select
            value={examFilter}
            onChange={e => setExamFilter(e.target.value)}
            className="sm:w-64 px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
          >
            <option value="">All exams</option>
            {exams.map((ex: any) => (
              <option key={ex.id} value={ex.id}>{ex.code} — {ex.title}</option>
            ))}
          </select>
          {(examFilter || q) && (
            <button
              onClick={() => { setQ(""); setExamFilter(""); }}
              className="px-3 py-2 border rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-colors self-start"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: stats.total, cls: "bg-gray-100 text-gray-600" },
          { label: "Open", value: stats.open, cls: "bg-green-100 text-green-600" },
          { label: "Upcoming", value: stats.upcoming, cls: "bg-amber-100 text-amber-600" },
          { label: "Closed", value: stats.closed, cls: "bg-red-100 text-red-600" },
        ].map(s => (
          <div key={s.label} className="bg-white border rounded-xl p-4 shadow-sm flex items-center gap-3">
            <div className={`p-2 rounded-lg ${s.cls}`}>
              <Calendar className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">{s.label}</p>
              <p className="text-2xl font-bold text-gray-900 leading-tight">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-20 flex flex-col items-center gap-2 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-sm">Loading exam calls…</p>
          </div>
        ) : loadError ? (
          <div className="py-16 text-center space-y-3">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
            <p className="text-sm text-red-500">Failed to load exam calls.</p>
            <button onClick={() => refetch()} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center space-y-2">
            <FileText className="w-10 h-10 mx-auto text-gray-200" />
            <p className="text-sm text-gray-400">
              {calls.length === 0 ? "No exam calls yet." : "No calls match your search or filter."}
            </p>
            {calls.length === 0 && canManage && (
              <button
                onClick={() => setModal({ mode: "create" })}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> Create First Call
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {["Exam", "Diet", "Title", "Opens", "Closes", "Fee", "Status", ""].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {paged.map((call: any) => {
                    const st = callStatus(call);
                    return (
                      <tr key={call.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          <span className="text-xs font-mono text-indigo-600 mr-1">{call.exam?.code}</span>
                          {call.exam?.title ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{call.diet?.title ?? "—"}</td>
                        <td className="px-4 py-3 text-gray-500">{call.title || <span className="text-gray-300 italic">—</span>}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(call.opens_at)}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(call.closes_at)}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {call.application_fee != null
                            ? <span>₦{Number(call.application_fee).toLocaleString()}</span>
                            : <span className="text-gray-300">Free</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${st.cls}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setModal({ mode: "view", call })}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {canManage && (
                              <>
                                <button
                                  onClick={() => { setModal({ mode: "edit", call }); setFormError(null); }}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => { setModal({ mode: "delete", call }); setDeleteError(null); }}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {paged.map((call: any) => {
                const st = callStatus(call);
                return (
                  <div key={call.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{call.exam?.title ?? "—"}</p>
                        <p className="text-xs text-gray-500">{call.diet?.title ?? "—"}{call.title ? ` · ${call.title}` : ""}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0 ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span><Clock className="w-3 h-3 inline mr-1" />{fmtDate(call.opens_at)} – {fmtDate(call.closes_at)}</span>
                      {call.application_fee != null && (
                        <span>₦{Number(call.application_fee).toLocaleString()}</span>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => setModal({ mode: "view", call })}
                        className="flex-1 py-1.5 border rounded-lg text-xs text-indigo-600 hover:bg-indigo-50 transition-colors"
                      >View</button>
                      <button
                        onClick={() => { setModal({ mode: "edit", call }); setFormError(null); }}
                        className="flex-1 py-1.5 border rounded-lg text-xs hover:bg-gray-50 transition-colors"
                      >Edit</button>
                      <button
                        onClick={() => { setModal({ mode: "delete", call }); setDeleteError(null); }}
                        className="py-1.5 px-3 border border-red-100 rounded-lg text-xs text-red-600 hover:bg-red-50 transition-colors"
                      >Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50/50">
              <p className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-700">{(safePage - 1) * PER_PAGE + 1}</span>–
                <span className="font-medium text-gray-700">
                  {Math.min(safePage * PER_PAGE, filtered.length)}
                </span>{" "}
                of <span className="font-medium text-gray-700">{filtered.length}</span> call
                {filtered.length !== 1 ? "s" : ""}
              </p>

              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    // Keep the strip short on long lists: first, last and a window around the current page.
                    .filter(n => n === 1 || n === pageCount || Math.abs(n - safePage) <= 1)
                    .map((n, i, arr) => (
                      <React.Fragment key={n}>
                        {i > 0 && n - arr[i - 1] > 1 && (
                          <span className="px-1 text-xs text-gray-300">…</span>
                        )}
                        <button
                          onClick={() => setPage(n)}
                          className={`min-w-[2rem] px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                            n === safePage
                              ? "bg-gray-900 text-white border-gray-900"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {n}
                        </button>
                      </React.Fragment>
                    ))}

                  <button
                    onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                    disabled={safePage === pageCount}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Modals ── */}
      <Modal open={modal.mode === "create"} title="New Exam Call" onClose={closeModal}>
        <CallForm
          exams={exams}
          diets={diets}
          onSubmit={handleCreate}
          onCancel={closeModal}
          submitting={creating}
          error={formError}
        />
      </Modal>

      <Modal open={modal.mode === "edit"} title="Edit Exam Call" onClose={closeModal}>
        {modal.call && (
          <CallForm
            initial={modal.call}
            exams={exams}
            diets={diets}
            onSubmit={handleEdit}
            onCancel={closeModal}
            submitting={updating}
            error={formError}
          />
        )}
      </Modal>

      <Modal open={modal.mode === "delete"} title="Delete Exam Call" onClose={closeModal}>
        {modal.call && (
          <DeleteConfirm
            call={modal.call}
            onConfirm={handleDelete}
            onCancel={closeModal}
            loading={deleting}
            error={deleteError}
          />
        )}
      </Modal>

      {/* View modal */}
      <Modal open={modal.mode === "view"} title="Exam Call Details" onClose={closeModal}>
        {modal.call && (() => {
          const c = modal.call;
          const st = callStatus(c);
          return (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span>
                {c.is_active
                  ? <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle2 className="w-3.5 h-3.5" />Active</span>
                  : <span className="text-xs text-gray-400">Inactive</span>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Exam</p>
                  <p className="font-medium text-gray-900">{c.exam?.code} — {c.exam?.title ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Diet</p>
                  <p className="font-medium text-gray-900">{c.diet?.title ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Opens At</p>
                  <p className="text-gray-700">{fmtDateTime(c.opens_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Closes At</p>
                  <p className="text-gray-700">{fmtDateTime(c.closes_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Application Fee</p>
                  <p className="text-gray-700">{c.application_fee != null ? `₦${Number(c.application_fee).toLocaleString()}` : "Free"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Applications</p>
                  <p className="text-gray-700">{c.applications?.length ?? 0}</p>
                </div>
              </div>

              {c.title && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Title</p>
                  <p className="text-gray-700">{c.title}</p>
                </div>
              )}
              {c.instructions && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Instructions</p>
                  <p className="text-gray-700 whitespace-pre-line">{c.instructions}</p>
                </div>
              )}
              {Array.isArray(c.requirements) && c.requirements.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Requirements</p>
                  <ul className="space-y-1 list-disc list-inside text-gray-700">
                    {c.requirements.map((r: string, i: number) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}

              <div className="flex gap-3 pt-2 border-t">
                <button
                  onClick={() => { setModal({ mode: "edit", call: c }); setFormError(null); }}
                  className="flex-1 py-2 rounded-lg border text-sm hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={closeModal} className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 transition-colors">
                  Close
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
